import { PageLoaderComponent, PageLoad } from '@app/shared/components/page-loader/page-loader.component';
import { PageTitleComponent } from '@app/shared/components/page-title/page-title.component';
import { inject, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ModulesService } from '@shared/services/modules.service';
import { UsersService } from '@shared/services/entities/users.service';
import { RolesService } from '@shared/services/entities/roles.service';
import { Router } from '@angular/router';


import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '@app/material.module';
/**
 * Tarjetas del menú de auditoría.
 *
 * Antes se derivaban del árbol de navegación que `NavigationService` pedía
 * al `epione-core-api`: se filtraban las rutas del remote
 * `MF_EPIONE_EVOLUTION` y con eso se decidía qué tarjetas mostrar. Ese
 * servicio era del shell de Module Federation y ya no existe.
 *
 * Las rutas hijas de `audit` hoy son estáticas (ver `app.routes.ts`), y el
 * título, la descripción y el icono de cada tarjeta ya estaban escritos a
 * mano aquí. O sea que la vuelta al core-api solo servía para decidir cuáles
 * de estas dos aparecían: ahora se declaran directo.
 */
const AUDIT_CARDS: card[] = [
  {
    title: 'Movimiento de insumos',
    description: 'Entradas, traspasos, vales y cirugías de cada pieza; busca por RFID, código o lote.',
    icon: 'inventory',
    route: 'rfid'
  },
  {
    title: 'Historial de ediciones',
    description: 'Altas, ediciones y borrados de cada módulo: qué cambió, quién y cuándo.',
    icon: 'history',
    route: 'history'
  },
  {
    title: 'Historial de Acceso',
    description: 'Qué usuarios entran a la plataforma, cuándo y desde qué navegador.',
    icon: 'login',
    route: 'access'
  },
];

export interface card {
  title: String,
  description: String,
  icon: String,
  route: String
}

@Component({
  standalone: true,
  imports: [PageLoaderComponent, PageTitleComponent, 
    FormsModule,
    ReactiveFormsModule,
    MaterialModule
],
  selector: 'app-audit-menu',
  templateUrl: './audit-menu.component.html',
  styleUrls: ['./audit-menu.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class AuditMenuComponent implements OnInit {
  private modulesSvcTitle = inject(ModulesService);
  private usersService = inject(UsersService);
  private rolesService = inject(RolesService);
  /** Título del módulo = etiqueta visible del catálogo de Módulos. */
  title = this.modulesSvcTitle.moduleTitle('/audit', 'Auditoría');


  componentRoutes = [];
  menuCards: card[];
 

  constructor(
    private router: Router
  ) { }

  /**
   * Cada opción es un sub-módulo en la tabla `modules` (/audit/rfid,
   * /audit/history, /audit/access): de ahí salen etiqueta, ícono y
   * descripción, y solo se muestran las activas que el rol tiene asignadas.
   * Si todavía no se siembran, se muestran las tres por omisión.
   */
  /** Pantalla de carga del módulo: bloquea hasta terminar la carga inicial. */
  readonly pageLoad = new PageLoad();

  ngOnInit(): void {
    void this.init();
  }

  /** Carga inicial (también la usa "Reintentar"). */
  init(): Promise<boolean> {
    return this.pageLoad.run(() => this.initialLoad());
  }

  private async initialLoad(): Promise<void> {
    this.menuCards = AUDIT_CARDS;
    try {
      const resp: any = await this.modulesSvcTitle.search({ searchType: 'default', condition: { deleted: false } });
      const norm = (r: any) => String(r ?? '').split('?')[0].replace(/\/+$/, '');
      const subs = ((resp?.data ?? []) as any[]).filter(m => AUDIT_CARDS.some(c => '/audit/' + c.route === norm(m.route)));
      if (subs.length === 0) return;
      const user = this.usersService.currentUser() ?? await this.usersService.loadCurrentUser();
      let nav: string[] | null = null;
      if (user?.role) {
        const rolesResp: any = await this.rolesService.search({ searchType: 'default', condition: { id: user.role } });
        const role = rolesResp?.data?.[0];
        nav = Array.isArray(role?.navAccessTree) ? role.navAccessTree : null;
      }
      this.menuCards = AUDIT_CARDS.map(c => {
        const mod = subs.find(m => norm(m.route) === '/audit/' + c.route);
        if (!mod) return c;
        if (mod.active === false) return null;
        if (nav && !nav.includes(mod.id)) return null;
        return { title: mod.label || mod.name || c.title, description: mod.description || c.description, icon: mod.icon || c.icon, route: c.route };
      }).filter((c): c is card => !!c);
    } catch { /* se quedan las tarjetas por omisión */ }
    this.loaded = true;
  }
  loaded = false;

  /**
   * @description Navigation to the children
   */
  navigationTo(route){
    //console.log(route)
    this.router.navigate(['/audit/' + route]);
  }
}